import { apiFetch, type JsonPatchOperation } from '@graal/api';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';

/**
 * Catalogue — tags `layer`, `database`, `table`, `field` et `glossary` de `merged.yaml` : couches
 * (`/layers`), bases, tables, champs, chacun en CRUD (`find*`, `create*`, `update*`, `delete*ById`),
 * plus la couche de gouvernance du 2026-10-19 (contrat 929e8437) : synchronisation d'une couche,
 * snapshots, statistiques, aperçu et usages d'une table, facettes de recherche (le glossaire vit dans
 * `glossary-api.ts`). Le « layer » du contrat est présenté comme un **catalogue** par la console
 * (Unity Catalog, Trino : catalogue > schéma > table) ; la base garde son nom.
 *
 * Client : `@graal/api` (`apiFetch`, mutator qui pose `X-Tenant`, `Accept` et le média type
 * d'écriture). Les types ci-dessous portent les NOMS des schémas du contrat (`Table1`, `Field`,
 * `CatalogSynchronization`…) et n'en prennent qu'un sous-ensemble : `catalog-contract.test.ts` valide
 * un exemplaire complet de chacun contre `merged.yaml` (une propriété inventée y échoue). Les hooks
 * orval générés (`packages/api/src/generated/`) ne sont pas importés : ils sont gitignorés, et la CI
 * `graal-pnpm-web` joue `typecheck` sans génération préalable — décision du J1, `src/api/contract.ts`.
 *
 * Chaque segment d'URL est résolu par le backend en `technical_name = :name OR id = :id`
 * (`R2Dbc*RepositoryAdapterImpl`) : la console écrit le nom technique, lisible dans l'URL.
 */

export type CatalogLevel = 'layer' | 'database' | 'table' | 'field';

/** Socle commun à `Layer`, `Database`, `Table1` et `Field` du contrat. */
export type ContractCatalogEntry = {
  id?: string;
  technical_name?: string;
  name?: string;
  description?: string;
  created?: string;
  updated?: string;
  metadata?: Record<string, unknown>;
};

/** `CatalogPrincipal` : un utilisateur ou un groupe du locataire. */
export type CatalogPrincipal = { type: 'user' | 'group'; id: string };
export const CERTIFICATIONS = ['certified', 'deprecated', 'none'] as const;
export type Certification = (typeof CERTIFICATIONS)[number];
/** `TableCertification` : `by` et `at` sont posés par le serveur. */
export type TableCertification = { status?: Certification; note?: string; by?: string; at?: string };
export type GlossaryTermRef = { id?: string; name?: string };
/** `TableEngine` : catalogue Trino, namespace Iceberg (= schéma Trino), table. */
export type TableEngine = { catalog?: string; namespace?: string; name?: string };
export const CLASSIFICATIONS = ['public', 'internal', 'confidential', 'sensitive', 'pii'] as const;
export type Classification = (typeof CLASSIFICATIONS)[number];
export type EntryStatus = 'active' | 'dropped';

/** `Table1` du contrat (le `Table` nu, `{name}`, est celui de l'entrepôt SQL). */
export type ContractCatalogTable = ContractCatalogEntry & {
  uri?: string;
  type?: string;
  format?: string;
  public_uri?: string;
  /** Nom complet côté moteur, tel qu'on le tape en SQL (`iceberg.t_<locataire>.<table>`). */
  qualified_name?: string;
  engine?: TableEngine;
  status?: EntryStatus;
  dropped_at?: string;
  properties?: Record<string, string>;
  owner?: CatalogPrincipal;
  stewards?: CatalogPrincipal[];
  tags?: string[];
  glossary_terms?: GlossaryTermRef[];
  certification?: TableCertification;
};
/** `Field` du contrat. */
export type ContractCatalogField = ContractCatalogEntry & {
  type?: string;
  nullable?: boolean;
  classification?: Classification;
  tags?: string[];
  glossary_terms?: GlossaryTermRef[];
  status?: EntryStatus;
  dropped_at?: string;
  trino_type?: string;
  position?: number;
};

export type SyncStatus = 'never' | 'running' | 'queued' | 'completed' | 'failed' | 'disabled';
/** `CatalogSynchronization` : la dernière réconciliation d'une couche avec le catalogue du moteur. */
export type CatalogSynchronization = {
  layer?: string;
  status?: SyncStatus;
  trigger?: 'manual' | 'schedule' | 'run' | 'event';
  started?: string;
  finished?: string;
  tables_seen?: number;
  tables_created?: number;
  tables_updated?: number;
  tables_unchanged?: number;
  tables_dropped?: number;
  fields_dropped?: number;
  truncated?: boolean;
  error?: string;
};

export type TableSnapshotSummary = {
  added_records?: number;
  deleted_records?: number;
  total_records?: number;
  added_files?: number;
  deleted_files?: number;
  total_files?: number;
  added_size_bytes?: number;
  removed_size_bytes?: number;
  total_size_bytes?: number;
};
/** `TableSnapshot` : un commit Iceberg. `snapshot_id` est un entier 64 bits, en chaîne. */
export type TableSnapshot = {
  snapshot_id?: string;
  parent_id?: string;
  committed_at?: string;
  operation?: string;
  current?: boolean;
  schema_id?: number;
  summary?: TableSnapshotSummary;
};

export type TablePartitionField = { name?: string; source_column?: string; transform?: string };
export type TableSortField = {
  source_column?: string;
  transform?: string;
  direction?: 'asc' | 'desc';
  null_order?: 'nulls-first' | 'nulls-last';
};
export type ColumnStatistics = {
  name?: string;
  field_id?: number;
  value_count?: number;
  null_count?: number;
  nan_count?: number;
  distinct_count?: number;
  min?: string;
  max?: string;
  size_bytes?: number;
};
export type TableStatistics = {
  snapshot_id?: string;
  record_count?: number;
  file_count?: number;
  delete_file_count?: number;
  total_size_bytes?: number;
  partition_spec?: TablePartitionField[];
  sort_order?: TableSortField[];
  columns?: ColumnStatistics[];
  files_scanned?: number;
  complete?: boolean;
  computed_at?: string;
};

/** `TablePreview` : colonnes `DataWarehouseQueryColumn` et lignes, sous l'identité de l'appelant. */
export type TablePreview = {
  qualified_name?: string;
  limit?: number;
  columns?: { name?: string; type?: string }[];
  rows?: unknown[][];
  truncated?: boolean;
};

export type TableUsage = { queries_7d?: number; users_7d?: number; last_queried?: string };

/** Position dans l'arbre : chaque niveau exige ses parents. */
export type CatalogPath =
  | { level: 'layer'; layer: string }
  | { level: 'database'; layer: string; database: string }
  | { level: 'table'; layer: string; database: string; table: string }
  | { level: 'field'; layer: string; database: string; table: string; field: string };

/** Le parent d'une collection : `undefined` pour les couches (racine). */
export type CatalogParent =
  | undefined
  | { level: 'layer'; layer: string }
  | { level: 'database'; layer: string; database: string }
  | { level: 'table'; layer: string; database: string; table: string };

/** Niveau enfant d'un parent (`undefined` → couches). */
export const childLevel = (parent: CatalogParent): CatalogLevel =>
  parent === undefined
    ? 'layer'
    : parent.level === 'layer'
      ? 'database'
      : parent.level === 'database'
        ? 'table'
        : 'field';

const enc = encodeURIComponent;

/** Chemin REST de la collection des enfants d'un parent. */
export function collectionUrl(parent: CatalogParent): string {
  if (parent === undefined) return '/layers';
  if (parent.level === 'layer') return `/layers/${enc(parent.layer)}/databases`;
  if (parent.level === 'database')
    return `/layers/${enc(parent.layer)}/databases/${enc(parent.database)}/tables`;
  return `/layers/${enc(parent.layer)}/databases/${enc(parent.database)}/tables/${enc(parent.table)}/fields`;
}

/** Chemin REST d'une entrée. */
export function entryUrl(path: CatalogPath): string {
  switch (path.level) {
    case 'layer':
      return `/layers/${enc(path.layer)}`;
    case 'database':
      return `${collectionUrl({ level: 'layer', layer: path.layer })}/${enc(path.database)}`;
    case 'table':
      return `${collectionUrl({ level: 'database', layer: path.layer, database: path.database })}/${enc(path.table)}`;
    case 'field':
      return `${collectionUrl({ level: 'table', layer: path.layer, database: path.database, table: path.table })}/${enc(path.field)}`;
  }
}

/** Parent d'une entrée (`undefined` pour une couche). */
export function parentOf(path: CatalogPath): CatalogParent {
  switch (path.level) {
    case 'layer':
      return undefined;
    case 'database':
      return { level: 'layer', layer: path.layer };
    case 'table':
      return { level: 'database', layer: path.layer, database: path.database };
    case 'field':
      return { level: 'table', layer: path.layer, database: path.database, table: path.table };
  }
}

/** Ressource du média type (`application/vnd.graal.systems.v1.<ressource>+json`). */
export const RESOURCE: Record<CatalogLevel, string> = {
  layer: 'layer',
  database: 'database',
  table: 'table',
  field: 'field',
};

/** Tableau nu à chaque niveau (`find*` → `collectList`). */
export const listCatalog = (parent: CatalogParent, signal?: AbortSignal) =>
  apiFetch<ContractCatalogEntry[]>({ url: collectionUrl(parent), method: 'GET', signal });

export const getCatalogEntry = (path: CatalogPath, signal?: AbortSignal) =>
  apiFetch<ContractCatalogEntry>({ url: entryUrl(path), method: 'GET', signal });

/** Création : 201 et l'entrée créée (le backend répond `created`, le contrat déclare 200). */
export const createCatalogEntry = (parent: CatalogParent, input: Record<string, unknown>) =>
  apiFetch<ContractCatalogEntry>({
    url: collectionUrl(parent),
    method: 'POST',
    data: input,
    resource: RESOURCE[childLevel(parent)],
  });

/** Édition en JSON Patch RFC 6902 — jamais de PUT complet (règle 5 de `web/CLAUDE.md`). */
export const patchCatalogEntry = (path: CatalogPath, operations: JsonPatchOperation[]) =>
  apiFetch<ContractCatalogEntry>({ url: entryUrl(path), method: 'PATCH', data: operations });

/** Suppression : 202 sans corps. Côté backend, rien n'est vérifié en RBAC (revue DM-05). */
export const deleteCatalogEntry = (path: CatalogPath) =>
  apiFetch<void>({ url: entryUrl(path), method: 'DELETE' });

// --- segments d'URL de la console -------------------------------------------

/**
 * Segment de la console pour une entrée : son nom technique, lisible — sauf `new`, qui est le
 * segment des formulaires de création (`…/new`) et masquerait l'entrée ; l'identifiant, que le
 * backend résout aussi, prend alors le relais.
 */
export function segmentOf(entry: ContractCatalogEntry): string | undefined {
  if (entry.technical_name && entry.technical_name !== 'new') return entry.technical_name;
  return entry.id ?? undefined;
}

/** URL de la console d'une entrée du catalogue. */
export function consoleHref(tenant: string, path: CatalogPath): string {
  const segments = [path.layer];
  if (path.level !== 'layer') segments.push(path.database);
  if (path.level === 'table' || path.level === 'field') segments.push(path.table);
  if (path.level === 'field') segments.push(path.field);
  return `/${tenant}/catalog/${segments.map(enc).join('/')}`;
}

/** URL de la console de la collection qui contient les enfants d'un parent (ou la racine). */
export function parentHref(tenant: string, parent: CatalogParent): string {
  return parent === undefined ? `/${tenant}/catalog` : consoleHref(tenant, parent);
}

/** Chemin d'un enfant d'un parent, à partir de son segment. */
export function childPath(parent: CatalogParent, segment: string): CatalogPath {
  if (parent === undefined) return { level: 'layer', layer: segment };
  if (parent.level === 'layer') return { level: 'database', layer: parent.layer, database: segment };
  if (parent.level === 'database')
    return { level: 'table', layer: parent.layer, database: parent.database, table: segment };
  return {
    level: 'field',
    layer: parent.layer,
    database: parent.database,
    table: parent.table,
    field: segment,
  };
}

// --- requêtes ----------------------------------------------------------------

const keyOf = (parent: CatalogParent) =>
  parent === undefined
    ? []
    : [
        parent.layer,
        ...('database' in parent ? [parent.database] : []),
        ...('table' in parent ? [parent.table] : []),
      ];

export const catalogKeys = {
  all: (tenant: string) => [tenant, 'catalog'] as const,
  children: (tenant: string, parent: CatalogParent) =>
    [tenant, 'catalog', 'children', ...keyOf(parent)] as const,
  entry: (tenant: string, path: CatalogPath) =>
    [tenant, 'catalog', 'entry', path.level, ...keyOf(parentOf(path)), entrySegment(path)] as const,
};

/** Dernier segment d'un chemin (le nom technique ou l'identifiant de l'entrée). */
export function entrySegment(path: CatalogPath): string {
  switch (path.level) {
    case 'layer':
      return path.layer;
    case 'database':
      return path.database;
    case 'table':
      return path.table;
    case 'field':
      return path.field;
  }
}

export const catalogChildrenQuery = (tenant: string, parent: CatalogParent) =>
  queryOptions({
    queryKey: catalogKeys.children(tenant, parent),
    queryFn: ({ signal }) => listCatalog(parent, signal),
  });

export const catalogEntryQuery = (tenant: string, path: CatalogPath) =>
  queryOptions({
    queryKey: catalogKeys.entry(tenant, path),
    queryFn: ({ signal }) => getCatalogEntry(path, signal),
  });

// --- gouvernance d'une table (2026-10-19) ------------------------------------

export type TablePath = Extract<CatalogPath, { level: 'table' }>;

/** `GET …/tables/{t}/snapshots?limit` — l'historique Iceberg, du plus récent au plus ancien. */
export const SNAPSHOT_LIMIT = 50;
export const listTableSnapshots = (path: TablePath, signal?: AbortSignal) =>
  apiFetch<TableSnapshot[]>({
    url: `${entryUrl(path)}/snapshots`,
    method: 'GET',
    params: { limit: SNAPSHOT_LIMIT },
    signal,
  });

/** `GET …/tables/{t}/statistics` — métadonnées Iceberg du snapshot courant. */
export const getTableStatistics = (path: TablePath, signal?: AbortSignal) =>
  apiFetch<TableStatistics>({ url: `${entryUrl(path)}/statistics`, method: 'GET', signal });

/** Plafond du contrat (`limit` 1 à 100) : l'aperçu en demande le maximum. */
export const PREVIEW_LIMIT = 100;
/** `GET …/tables/{t}/preview?limit` — `SELECT * … LIMIT n` par le Trino de la plateforme. */
export const previewTable = (path: TablePath, signal?: AbortSignal) =>
  apiFetch<TablePreview>({
    url: `${entryUrl(path)}/preview`,
    method: 'GET',
    params: { limit: PREVIEW_LIMIT },
    signal,
  });

/**
 * `GET …/tables/{t}/usage` — DÉCLARÉ au contrat, servi en 501 `feature_disabled` (`catalog.usage`)
 * tant qu'aucun event listener Trino ne l'alimente. Appel volontaire sur une fonction coupée (règle 3
 * de la console) : l'onglet montre l'état réel, « bientôt », plutôt qu'un écran vide.
 */
export const getTableUsage = (path: TablePath, signal?: AbortSignal) =>
  apiFetch<TableUsage>({ url: `${entryUrl(path)}/usage`, method: 'GET', signal });

// --- synchronisation d'une couche ----------------------------------------------

/** Seule la couche `iceberg` est synchronisée (miroir du namespace Iceberg du locataire). */
export const SYNCHRONIZED_LAYER = 'iceberg';
export const isSynchronizedLayer = (layer: string) => layer === SYNCHRONIZED_LAYER;

const synchronizationUrl = (layer: string) => `/layers/${enc(layer)}/synchronization`;

export const getLayerSynchronization = (layer: string, signal?: AbortSignal) =>
  apiFetch<CatalogSynchronization>({ url: synchronizationUrl(layer), method: 'GET', signal });

/** `POST …/synchronization`, sans corps : `completed`, `failed` ou `queued` (une autre tourne). */
export const synchronizeLayer = (layer: string) =>
  apiFetch<CatalogSynchronization>({ url: synchronizationUrl(layer), method: 'POST' });

// --- recherche ---------------------------------------------------------------

/** Les facettes de `GET /tables` et `GET /tables/facets`, dans l'ordre de l'écran. */
export const CATALOG_FACETS = [
  'layer',
  'format',
  'tag',
  'owner',
  'certification',
  'quality',
  'classification',
] as const;
export type CatalogFacet = (typeof CATALOG_FACETS)[number];

/** Paramètres d'une recherche, tels qu'ils vivent dans l'URL de la console. */
export type CatalogSearchParams = Partial<Record<CatalogFacet, string>> & {
  search?: string;
  column?: string;
  include_dropped?: boolean;
};

/** `TableSearchResult` du contrat. */
export type TableSearchResult = {
  layer_id?: string;
  layer?: string;
  database?: string;
  table?: string;
  description?: string;
  matched_fields?: string[];
  quality_status?: string;
  table_id?: string;
  database_id?: string;
  qualified_name?: string;
  format?: string;
  status?: EntryStatus;
  owner?: CatalogPrincipal;
  certification?: Certification;
  tags?: string[];
  classifications?: string[];
};
export type FacetCount = { value?: string; count?: number };
/** `TableSearchFacets` : le total et les comptes par facette, sans la limite de la recherche. */
export type TableSearchFacets = {
  total?: number;
  facets?: Partial<Record<CatalogFacet, FacetCount[]>>;
};

export const SEARCH_LIMIT = 50;

/** Une recherche a-t-elle un critère ? Sans critère, l'écran montre les catalogues, pas des résultats. */
export const hasSearchCriteria = (params: CatalogSearchParams) =>
  Boolean(params.search || params.column || CATALOG_FACETS.some((facet) => params[facet]));

/** Les paramètres de query d'une recherche : seulement ceux qui sont posés. */
export function searchQueryParams(params: CatalogSearchParams): Record<string, string | boolean> {
  const query: Record<string, string | boolean> = {};
  if (params.search) query['search'] = params.search;
  if (params.column) query['column'] = params.column;
  for (const facet of CATALOG_FACETS) {
    const value = params[facet];
    if (value) query[facet] = value;
  }
  if (params.include_dropped) query['include_dropped'] = true;
  return query;
}

export const searchCatalogTables = (params: CatalogSearchParams, signal?: AbortSignal) =>
  apiFetch<TableSearchResult[]>({
    url: '/tables',
    method: 'GET',
    params: { ...searchQueryParams(params), limit: SEARCH_LIMIT },
    signal,
  });

export const findCatalogFacets = (params: CatalogSearchParams, signal?: AbortSignal) =>
  apiFetch<TableSearchFacets>({
    url: '/tables/facets',
    method: 'GET',
    params: searchQueryParams(params),
    signal,
  });

/** Un principal du contrat ↔ sa forme de facette et de formulaire (`user:<id>`, `group:<id>`). */
export const principalKey = (principal: CatalogPrincipal | undefined) =>
  principal ? `${principal.type}:${principal.id}` : '';
export function parsePrincipal(value: unknown): CatalogPrincipal | undefined {
  if (typeof value !== 'string') return undefined;
  const match = /^(user|group):(.+)$/u.exec(value.trim());
  return match ? { type: match[1] as CatalogPrincipal['type'], id: match[2]! } : undefined;
}

// --- requêtes de gouvernance ---------------------------------------------------
// Le défaut de `lib/query-client.ts` ne retente ni un 403, ni un 404, ni un 501 : l'onglet les affiche.

const tableKey = (tenant: string, path: TablePath, what: string) =>
  [...catalogKeys.entry(tenant, path), what] as const;

export const tableSnapshotsQuery = (tenant: string, path: TablePath) =>
  queryOptions({
    queryKey: tableKey(tenant, path, 'snapshots'),
    queryFn: ({ signal }) => listTableSnapshots(path, signal),
  });

export const tableStatisticsQuery = (tenant: string, path: TablePath) =>
  queryOptions({
    queryKey: tableKey(tenant, path, 'statistics'),
    queryFn: ({ signal }) => getTableStatistics(path, signal),
  });

export const tablePreviewQuery = (tenant: string, path: TablePath) =>
  queryOptions({
    queryKey: tableKey(tenant, path, 'preview'),
    queryFn: ({ signal }) => previewTable(path, signal),
    // Un aperçu coûte une requête Trino : il n'est pas relu à chaque retour sur l'onglet.
    staleTime: 60_000,
  });

export const tableUsageQuery = (tenant: string, path: TablePath) =>
  queryOptions({
    queryKey: tableKey(tenant, path, 'usage'),
    queryFn: ({ signal }) => getTableUsage(path, signal),
  });

export const layerSynchronizationQuery = (tenant: string, layer: string) =>
  queryOptions({
    queryKey: [tenant, 'catalog', 'synchronization', layer] as const,
    queryFn: ({ signal }) => getLayerSynchronization(layer, signal),
    // Une synchronisation en cours ou en file est relue toutes les 3 s.
    refetchInterval: (query) =>
      query.state.data?.status === 'running' || query.state.data?.status === 'queued' ? 3_000 : false,
  });

export const catalogSearchKeys = {
  results: (tenant: string, params: CatalogSearchParams) =>
    [tenant, 'catalog', 'search', searchQueryParams(params)] as const,
  facets: (tenant: string, params: CatalogSearchParams) =>
    [tenant, 'catalog', 'facets', searchQueryParams(params)] as const,
};

export const catalogSearchQuery = (tenant: string, params: CatalogSearchParams) =>
  queryOptions({
    queryKey: catalogSearchKeys.results(tenant, params),
    queryFn: ({ signal }) => searchCatalogTables(params, signal),
    enabled: hasSearchCriteria(params),
    placeholderData: keepPreviousData,
  });

export const catalogFacetsQuery = (tenant: string, params: CatalogSearchParams) =>
  queryOptions({
    queryKey: catalogSearchKeys.facets(tenant, params),
    queryFn: ({ signal }) => findCatalogFacets(params, signal),
    enabled: hasSearchCriteria(params),
    placeholderData: keepPreviousData,
  });

/** Adresse de la console d'un résultat : catalogue > base > table, par leurs noms techniques. */
export function searchResultHref(tenant: string, row: TableSearchResult): string | undefined {
  if (!row.layer || !row.database || !row.table) return undefined;
  return consoleHref(tenant, { level: 'table', layer: row.layer, database: row.database, table: row.table });
}

/** `validateSearch` de `/$tenant/catalog/` : chaque critère est facultatif, une chaîne vide est retirée. */
export function parseCatalogSearch(search: Record<string, unknown>): CatalogSearchParams {
  const out: CatalogSearchParams = {};
  const read = (key: string) => (typeof search[key] === 'string' ? (search[key] as string).trim() : '');
  const text = read('search');
  if (text) out.search = text;
  const column = read('column');
  if (column) out.column = column;
  for (const facet of CATALOG_FACETS) {
    const value = read(facet);
    if (value) out[facet] = value;
  }
  if (search['include_dropped'] === true || search['include_dropped'] === 'true') out.include_dropped = true;
  return out;
}
